/** @jsxImportSource @emotion/react */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { css } from "@emotion/react";
import BaseButton from "components/common/Button/Base";
import BaseInput from "components/common/Input/Base";
import BaseSelect from "components/common/Select/Base";
import BaseCheck from "components/common/Check/Base";
import AlertDialog, { AlertData } from "components/common/Dialog/Alert";
import ConfirmDialog from "components/common/Dialog/Confirm";
import { DialogContentText } from "@mui/material";
import PayrollGrid, { SortState } from "./PayrollGrid";
import MemoPopover from "./MemoPopover";
import SeveranceRequestDialog from "./Dialog/SeveranceRequest";
import KakaoSendDialog from "./Dialog/KakaoSend";
import KakaoHistoryDialog from "./Dialog/KakaoHistory";
import BusinessDetailDialog from "./Dialog/BusinessDetail";
import UploadDialog from "./Dialog/Upload";
import MatchingDialog from "./Dialog/Matching";
import ResultDialog from "./Dialog/Result";
import DuplicateCheckDialog from "./Dialog/DuplicateCheck";
import { PALETTE } from "styles/base/variables";
import { MONTHS } from "data/period.data";
import { reverseArray } from "utils/converter";
import { useMyChargeOnlyFilter } from "utils/hook";
import {
  DerivedRow,
  MemoRepeat,
  PayrollGridRow,
  PayrollMemo,
  SeveranceRequestPanel,
  StatusCode
} from "models/payrollManagement.model";
import { MatchCandidateBusiness } from "./Dialog/SelectBusiness";
import PhoneEditDialog from "./Dialog/PhoneEdit";
import {
  BusinessWithPaymentNoticeCount,
  PaymentNotice as PaymentNoticeData,
  PaymentNoticeUploadResult,
  SimpleBusiness
} from "models/paymentNotice.model";
import { UserInfo } from "models/user.model";
import {
  addPayrollMemo,
  checkDuplicatePaymentNotices,
  deleteKakaoMobileNo,
  deletePayrollMemo,
  getBusinessPaymentNoticeCounts,
  getInchargeCandidates,
  getPayrollGrid,
  getSeveranceRequests,
  getSimpleBusinessList,
  getSmartCtaMemos,
  getUnmatchedPaymentNotices,
  getUserInfo,
  patchPayrollManualCheck,
  patchRetirementSettlement,
  setPayrollMemoDone,
  updateIncharge,
  updateKakaoMobileNo,
  updateSmartCtaMemo
} from "pages/remotes";
import { COLUMN_BY_ID, COLLAPSIBLE_GROUP_IDS, GROUPS } from "./grid.config";
import {
  deriveRow,
  groupState,
  isResendNeeded,
  rowIsDone,
  sendClass
} from "./grid.logic";

const DEFAULT_PERIOD = MONTHS[MONTHS.length - 1].value;

// 발송 요약 집계에서 클릭해 필터링하는 버킷
type SendBucket = "sent" | "resend" | "unsent" | "ready" | "check" | "noupload";

const parsePeriod = (period: string): [number, number] => {
  const [y, m] = period.split("-").map(Number);
  return [y, m];
};

const TYPE_ORDER: Record<string, number> = { 월별: 0, 반기: 1 };
const BASIS_ORDER: Record<string, number> = { 당월: 0, 전월: 1 };

// 담당자 선택 드롭다운에서 "담당자 없음"을 고르면 이 값을 pendingIncharge로
// 담아뒀다가, 실제 API 호출 시점에는 incharge를 null로 바꿔서 보낸다.
const NO_INCHARGE_OPTION_VALUE = "__NONE__";

const STATUS_LEGEND: { code: StatusCode; label: string; desc: string }[] = [
  { code: "done", label: "완료", desc: "입력완료·전송·신고·첨부·제출" },
  {
    code: "todo",
    label: "미완료",
    desc: "임시저장·미전송·미첨부·미신고·미제출"
  },
  { code: "refund", label: "환급", desc: "환급세액(0 이하) — 납부 불필요" },
  { code: "alert", label: "확인요망", desc: "소득·납부서 불일치" },
  { code: "off", label: "해당없음", desc: "대상 아님 · 급여자료 미입력" }
];

function PayrollManagement() {
  const [searchParams, setSearchParams] = useSearchParams();
  const period = searchParams.get("period") || DEFAULT_PERIOD;
  const [viewYear, viewMonth] = parsePeriod(period);

  const sort: SortState = useMemo(() => {
    const [key, d] = (searchParams.get("sort") || "name,1").split(",");
    return { key: key || "name", dir: d === "-1" ? -1 : 1 };
  }, [searchParams]);

  const [userInfo, setUserInfo] = useState<UserInfo | null>(null);
  const [gridRows, setGridRows] = useState<PayrollGridRow[]>([]);
  // 알림톡 수신 번호는 grid API에 없어 납부서 현황 API에서 따로 받아 병합한다.
  const [phoneMap, setPhoneMap] = useState<
    Map<string, { kakao: string | null; user: string | null }>
  >(new Map());
  // 거래처 자유 메모 (Organization.memoMap). grid API에 없어 §4.4에서 따로 받아 병합한다.
  const [memoMap, setMemoMap] = useState<Map<string, string>>(new Map());
  // 세특 [급여입력]의 퇴사자 정산요청 패널 (§2.9). 상단 버튼 배지 + 다이얼로그가 함께 쓴다.
  const [severancePanel, setSeverancePanel] =
    useState<SeveranceRequestPanel | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [candidateBusinesses, setCandidateBusinesses] = useState<
    MatchCandidateBusiness[]
  >([]);
  const [unmatchedCount, setUnmatchedCount] = useState(0);

  const [keyword, setKeyword] = useState("");
  const [onlyTodo, setOnlyTodo] = useState(false);
  const [isMyChargeOnly, setIsMyChargeOnly] = useMyChargeOnlyFilter();
  const [collapsed, setCollapsed] = useState<Set<string>>(
    () => new Set(COLLAPSIBLE_GROUP_IDS)
  );
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const [memoTarget, setMemoTarget] = useState<{
    row: DerivedRow;
    rect: DOMRect;
  } | null>(null);
  const [retDialogOpen, setRetDialogOpen] = useState(false);
  // 납부서 목록 팝업 (납부서 및 접수증 칸 클릭). null = 닫힘
  const [noticeListBusinessId, setNoticeListBusinessId] = useState<
    string | null
  >(null);
  // 발송한 알림톡 내용 팝업 (발송 칸 초록일 때 클릭). null = 닫힘
  const [kakaoHistoryTarget, setKakaoHistoryTarget] =
    useState<DerivedRow | null>(null);

  // 담당자 설정 모드: 켜지면 행 체크가 가능해지고, 선택한 거래처에 담당자를 일괄 배정한다.
  const [isAssignMode, setIsAssignMode] = useState(false);
  const [inchargeOptions, setInchargeOptions] = useState<string[]>([]);
  const [isInchargeOptionsLoading, setIsInchargeOptionsLoading] =
    useState(false);
  const [pendingIncharge, setPendingIncharge] = useState("");
  const [isInchargeConfirmOpen, setIsInchargeConfirmOpen] = useState(false);
  const [isInchargeSubmitting, setIsInchargeSubmitting] = useState(false);

  // 납부서 카톡 발송: 버튼을 누르면 선택 모드로 들어가고, 그때만 행 체크가 가능해진다.
  const [isSendMode, setIsSendMode] = useState(false);
  // 발송 요약 집계에서 누른 버킷 (null = 필터 없음). 선택 모드에서만 쓴다.
  const [sendFilter, setSendFilter] = useState<SendBucket | null>(null);
  // 카톡 발송 진행 다이얼로그 대상 (비어 있으면 닫힘)
  const [kakaoTargets, setKakaoTargets] = useState<
    { id: string; name: string }[]
  >([]);
  // 이미 발송한 거래처가 섞여 있을 때 재확인 팝업에 넘길 행 (비어 있으면 닫힘)
  const [pendingSendRows, setPendingSendRows] = useState<DerivedRow[]>([]);
  // 알림톡 수신 번호 변경 팝업 대상 행 (null = 닫힘)
  const [phoneEditTarget, setPhoneEditTarget] = useState<DerivedRow | null>(
    null
  );

  const [isAlertOpen, setIsAlertOpen] = useState(false);
  const [alertData, setAlertData] = useState<AlertData>({ message: "" });
  const alert = useCallback(
    (message: string, type: AlertData["type"] = "error") => {
      setAlertData({ message, type });
      setIsAlertOpen(true);
    },
    []
  );

  // 업로드/매칭/중복 오케스트레이션 상태 (기존 화면 로직 유지)
  const [isUploadOpen, setIsUploadOpen] = useState(false);
  const [isMatchingOpen, setIsMatchingOpen] = useState(false);
  const [unmatchedList, setUnmatchedList] = useState<PaymentNoticeData[]>([]);
  const [pendingResults, setPendingResults] = useState<
    PaymentNoticeUploadResult[]
  >([]);
  const [pendingDupIds, setPendingDupIds] = useState<string[]>([]);
  const [resultList, setResultList] = useState<PaymentNoticeUploadResult[]>([]);
  const [isResultOpen, setIsResultOpen] = useState(false);
  const [flowSource, setFlowSource] = useState<"upload" | "standalone">(
    "upload"
  );
  // 업로드 버튼을 눌렀는데 매칭 필요 납부서가 있어 매칭 화면을 먼저 띄운 경우.
  // 매칭 화면(취소) 또는 매칭 결과 화면이 닫히면 이어서 업로드 화면을 연다.
  const [openUploadAfterMatch, setOpenUploadAfterMatch] = useState(false);
  const [isDupOpen, setIsDupOpen] = useState(false);
  const [dupNotices, setDupNotices] = useState<PaymentNoticeData[]>([]);
  const [pendingResultAfterDup, setPendingResultAfterDup] = useState<
    PaymentNoticeUploadResult[]
  >([]);
  const [hasPendingResultAfterDup, setHasPendingResultAfterDup] =
    useState(false);

  useEffect(() => {
    getUserInfo()
      .then((res) => setUserInfo(res.data))
      .catch((err) => console.error(err));
    getSimpleBusinessList()
      .then((res) => {
        const list: SimpleBusiness[] = res.data?.businessDtos || res.data || [];
        setCandidateBusinesses(
          list.map((b) => ({
            id: b.id,
            name: b.name,
            regNumber: b.regNumber
          }))
        );
      })
      .catch((err) => console.error(err));
  }, []);

  const organizationId = userInfo?.taxOfficeOrgId || null;
  const myName = userInfo?.name || "";

  const loadPhoneMap = () => {
    getBusinessPaymentNoticeCounts(period, organizationId || undefined)
      .then((res) => {
        const list: BusinessWithPaymentNoticeCount[] = res.data || [];
        setPhoneMap(
          new Map(
            list.map((b) => [
              b.id,
              { kakao: b.kakaoMobileNo ?? null, user: b.userMobileNo ?? null }
            ])
          )
        );
      })
      .catch((err) => console.error(err));
  };

  const loadOrgMemos = () => {
    getSmartCtaMemos()
      .then((res) => {
        const obj: Record<string, string> = res.data || {};
        setMemoMap(new Map(Object.entries(obj)));
      })
      .catch((err) => console.error(err));
  };

  const loadGrid = () => {
    setIsLoading(true);
    getPayrollGrid(period, organizationId || undefined)
      .then((res) => setGridRows(res.data || []))
      .catch((err) => {
        console.error(err);
        alert(
          err.response?.data?.message ||
            "거래처 현황을 불러오는 중 오류가 발생했습니다."
        );
      })
      .finally(() => setIsLoading(false));
    loadPhoneMap();
    loadOrgMemos();
  };

  const checkUnmatched = () => {
    if (!organizationId) return;
    getUnmatchedPaymentNotices(organizationId, { size: 1 })
      .then((res) => setUnmatchedCount(res.data?.totalElements || 0))
      .catch((err) => console.error(err));
  };

  const loadSeverancePanel = () => {
    getSeveranceRequests(period, organizationId || undefined)
      .then((res) => setSeverancePanel(res.data || null))
      .catch((err) => console.error(err));
  };

  useEffect(() => {
    loadGrid();
    loadSeverancePanel();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period, organizationId]);

  useEffect(() => {
    checkUnmatched();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizationId]);

  // 담당자 설정 모드를 처음 켤 때 후보 목록을 한 번 불러온다.
  useEffect(() => {
    if (!isAssignMode || inchargeOptions.length > 0) return;
    setIsInchargeOptionsLoading(true);
    getInchargeCandidates()
      .then((res) => setInchargeOptions(res.data || []))
      .catch((err) => {
        console.error(err);
        alert(
          err.response?.data?.message ||
            "담당자 목록을 불러오던 중 오류가 발생했습니다."
        );
      })
      .finally(() => setIsInchargeOptionsLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAssignMode]);

  // ── 파생 + 필터 + 정렬 ────────────────────────────────────────
  const derivedRows: DerivedRow[] = useMemo(
    () =>
      gridRows.map((r) => {
        const d = deriveRow(r, { viewMonth });
        const p = phoneMap.get(r.businessId);
        return {
          ...d,
          kakaoMobileNo: p?.kakao ?? null,
          userMobileNo: p?.user ?? null,
          orgMemo: memoMap.get(r.businessId) ?? null
        };
      }),
    [gridRows, viewMonth, phoneMap, memoMap]
  );

  // 검색어·내 담당만 보기까지 적용한 목록 (상단 건수·조치필요 카운트는 이 기준)
  const filteredRows = useMemo(() => {
    let list = derivedRows.slice();
    if (isMyChargeOnly && myName)
      list = list.filter((r) => r.incharge === myName);
    if (keyword)
      list = list.filter((r) =>
        (r.name || "").toLowerCase().includes(keyword.toLowerCase())
      );
    return list;
  }, [derivedRows, isMyChargeOnly, myName, keyword]);

  const todoCount = useMemo(
    () => filteredRows.filter((r) => !rowIsDone(r, viewMonth)).length,
    [filteredRows, viewMonth]
  );

  // 발송 요약 집계 (선택 모드 상단). filteredRows 기준이라 담당자·검색 필터가 반영된다.
  const sendStats = useMemo(() => {
    const s = {
      sent: 0,
      resend: 0,
      unsent: 0,
      ready: 0,
      check: 0,
      noupload: 0
    };
    filteredRows.forEach((r) => {
      const c = sendClass(r);
      if (c === "sent") {
        s.sent += 1;
        if (isResendNeeded(r)) s.resend += 1;
      } else if (c === "unsent-ready") {
        s.unsent += 1;
        s.ready += 1;
      } else if (c === "unsent-check") {
        s.unsent += 1;
        s.check += 1;
      } else if (c === "unsent-noupload") {
        s.unsent += 1;
        s.noupload += 1;
      }
    });
    return s;
  }, [filteredRows]);

  const matchesSendBucket = useCallback((r: DerivedRow, bucket: SendBucket) => {
    const c = sendClass(r);
    switch (bucket) {
      case "sent":
        return c === "sent";
      case "resend":
        return c === "sent" && isResendNeeded(r);
      case "unsent":
        return (
          c === "unsent-ready" ||
          c === "unsent-check" ||
          c === "unsent-noupload"
        );
      case "ready":
        return c === "unsent-ready";
      case "check":
        return c === "unsent-check";
      case "noupload":
        return c === "unsent-noupload";
      default:
        return true;
    }
  }, []);

  const visibleRows = useMemo(() => {
    let list = onlyTodo
      ? filteredRows.filter((r) => !rowIsDone(r, viewMonth))
      : filteredRows.slice();

    if (isSendMode && sendFilter)
      list = list.filter((r) => matchesSendBucket(r, sendFilter));

    const byName = (a: DerivedRow, b: DerivedRow) =>
      a.name.localeCompare(b.name, "ko");
    const { key, dir } = sort;
    if (key.startsWith("grp:")) {
      const g = GROUPS.find((x) => x.id === key.slice(4));
      list.sort(
        (a, b) =>
          (g
            ? dir * (groupState(a, g, viewMonth) - groupState(b, g, viewMonth))
            : 0) || byName(a, b)
      );
    } else if (key === "incharge") {
      list.sort(
        (a, b) =>
          dir * (a.incharge || "").localeCompare(b.incharge || "", "ko") ||
          byName(a, b)
      );
    } else if (key === "type") {
      list.sort(
        (a, b) =>
          dir * (TYPE_ORDER[a.typeLabel] - TYPE_ORDER[b.typeLabel]) ||
          byName(a, b)
      );
    } else if (key === "basis") {
      list.sort(
        (a, b) =>
          dir * (BASIS_ORDER[a.basisLabel] - BASIS_ORDER[b.basisLabel]) ||
          byName(a, b)
      );
    } else {
      // 기본(거래처명) 정렬 — 반기 거래처는 그 달에 할 일이 아예 달라서 항상 맨 아래로.
      // 유형 순서는 dir을 곱하지 않아 이름 역순으로 바꿔도 월별이 먼저다.
      list.sort(
        (a, b) =>
          TYPE_ORDER[a.typeLabel] - TYPE_ORDER[b.typeLabel] ||
          dir * byName(a, b)
      );
    }
    return list;
  }, [
    filteredRows,
    onlyTodo,
    sort,
    viewMonth,
    isSendMode,
    sendFilter,
    matchesSendBucket
  ]);

  // 상단 버튼 배지 = 이번 지급월에 담당자 조치가 필요한 건 수
  //  탭1: 세특 정산 대기(PENDING) / 탭2: 이번 달 지급예정인데 미정산(DC 제외)
  const retCount = useMemo(() => {
    if (!severancePanel) return 0;
    const wage = severancePanel.incomeTaxSettlements.filter(
      (v) => v.wageProgress === "PENDING"
    ).length;
    const sev = severancePanel.severancePaySettlements.filter(
      (v) => v.payMonthIsCurrent && !v.settledAt && v.pensionType !== "DC"
    ).length;
    return wage + sev;
  }, [severancePanel]);

  // 발송 대상 = 현재 화면에 보이면서 선택된 거래처
  const selectedRows = useMemo(
    () => visibleRows.filter((r) => selected.has(r.businessId)),
    [visibleRows, selected]
  );

  // 선택했지만 지금 필터에 가려져 화면에 안 보이는 건수 (담당자 일괄 배정 시 안내용)
  const hiddenCheckedCount = useMemo(() => {
    const visibleIds = new Set(visibleRows.map((r) => r.businessId));
    return Array.from(selected).filter((id) => !visibleIds.has(id)).length;
  }, [visibleRows, selected]);

  // ── 이벤트 ───────────────────────────────────────────────────
  const setParam = (patch: Record<string, string>) => {
    const next = new URLSearchParams(searchParams);
    Object.entries(patch).forEach(([k, v]) => next.set(k, v));
    setSearchParams(next);
  };

  const onSort = (key: string) => {
    if (sort.key === key)
      setParam({ sort: `${key},${sort.dir === 1 ? -1 : 1}` });
    else setParam({ sort: `${key},1` });
  };

  const onToggleCollapse = (groupId: string) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      next.has(groupId) ? next.delete(groupId) : next.add(groupId);
      return next;
    });

  const onToggleSelected = useCallback((businessId: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(businessId) ? next.delete(businessId) : next.add(businessId);
      return next;
    });
  }, []);

  const onSelectRange = useCallback(
    (businessIds: string[], checked: boolean) => {
      setSelected((prev) => {
        const next = new Set(prev);
        businessIds.forEach((id) => (checked ? next.add(id) : next.delete(id)));
        return next;
      });
    },
    []
  );

  // ── 담당자 설정 ──────────────────────────────────────────────
  const toggleAssignMode = () => {
    setSelected(new Set());
    if (!isAssignMode) {
      // 발송 모드와 동시에 켤 수 없다.
      setIsSendMode(false);
      setSendFilter(null);
    }
    setPendingIncharge("");
    setIsAssignMode((prev) => !prev);
  };

  const selectIncharge = (e: React.MouseEvent<HTMLOptionElement>) => {
    const value = e.currentTarget.value || "";
    if (!value || selected.size === 0) return;
    setPendingIncharge(value);
    setIsInchargeConfirmOpen(true);
  };

  const closeInchargeConfirm = () => {
    if (isInchargeSubmitting) return;
    setIsInchargeConfirmOpen(false);
    setPendingIncharge("");
  };

  const confirmAssignIncharge = () => {
    if (!pendingIncharge || selected.size === 0) return;
    setIsInchargeSubmitting(true);
    const incharge =
      pendingIncharge === NO_INCHARGE_OPTION_VALUE ? null : pendingIncharge;
    const updateData = Array.from(selected).map((businessId) => ({
      businessId,
      incharge
    }));
    updateIncharge(updateData)
      .then(() => {
        setIsInchargeConfirmOpen(false);
        setPendingIncharge("");
        setSelected(new Set());
        setIsAssignMode(false);
        alert("담당자가 수정되었습니다. 변경 내용을 반영합니다.", "success");
        loadGrid();
      })
      .catch((err) => {
        console.error(err);
        alert(
          err.response?.data?.message || "담당자 수정 중 오류가 발생했습니다."
        );
      })
      .finally(() => setIsInchargeSubmitting(false));
  };

  const pendingInchargeLabel =
    pendingIncharge === NO_INCHARGE_OPTION_VALUE
      ? "담당자 없음"
      : pendingIncharge;

  // ── 납부서 카톡 발송 ─────────────────────────────────────────
  const exitSendMode = () => {
    setIsSendMode(false);
    setSelected(new Set());
    setSendFilter(null);
  };

  const toggleSendFilter = (b: SendBucket) =>
    setSendFilter((cur) => (cur === b ? null : b));

  // 발송 후 그리드가 갱신돼 필터 대상이 사라지면 필터를 자동 해제한다.
  useEffect(() => {
    if (sendFilter && sendStats[sendFilter] === 0) setSendFilter(null);
  }, [sendFilter, sendStats]);

  const openKakaoDialog = (targetRows: DerivedRow[]) =>
    setKakaoTargets(
      targetRows.map((r) => ({ id: r.businessId, name: r.name }))
    );

  const handleSendClick = () => {
    if (!selectedRows.length) return;
    if (selectedRows.some((r) => r.dispatched)) {
      setPendingSendRows(selectedRows);
      return;
    }
    openKakaoDialog(selectedRows);
  };

  // 퇴직 칸 클릭 = 세무사랑 처리 완료 토글 (스펙 §5.2 "담당자가 셀 클릭").
  // 그리드 행에는 payeeId가 없으므로 §2.9 패널에서 이번 달 지급예정(DC 제외) 퇴사자를
  // 찾아 일괄 토글한다. req → 전원 완료 / ok → 전원 해제.
  const retToggleRef = useRef<(row: DerivedRow) => void>(() => undefined);
  retToggleRef.current = (row: DerivedRow) => {
    const status = row.cells.ret;
    if (status !== "req" && status !== "ok") return; // off = 대상 아님
    const targets = (severancePanel?.severancePaySettlements ?? []).filter(
      (v) =>
        v.businessId === row.businessId &&
        v.payMonthIsCurrent &&
        v.pensionType !== "DC"
    );
    if (!targets.length) {
      // 패널이 아직 로딩 전이거나 데이터 불일치 — 상세 목록을 대신 보여준다.
      setRetDialogOpen(true);
      return;
    }
    const nextSettled = status !== "ok";
    Promise.all(
      targets.map((v) =>
        patchRetirementSettlement({
          businessId: v.businessId,
          payeeId: v.payeeId,
          settled: nextSettled
        })
      )
    )
      .then(() => {
        loadSeverancePanel();
        loadGrid();
      })
      .catch((err) => {
        console.error(err);
        alert(
          err.response?.data?.message ||
            "퇴직금 정산 처리 중 오류가 발생했습니다."
        );
      });
  };

  // GridRow(memo)에 넘기는 콜백은 참조를 고정한다.
  const onRetClick = useCallback(
    (row: DerivedRow) => retToggleRef.current(row),
    []
  );
  const onMemoOpen = useCallback(
    (row: DerivedRow, anchor: HTMLElement) =>
      setMemoTarget({ row, rect: anchor.getBoundingClientRect() }),
    []
  );
  const onNoticeCellClick = useCallback(
    (row: DerivedRow) => setNoticeListBusinessId(row.businessId),
    []
  );
  const onPhoneClick = useCallback(
    (row: DerivedRow) => setPhoneEditTarget(row),
    []
  );
  const onDispatchClick = useCallback(
    (row: DerivedRow) => setKakaoHistoryTarget(row),
    []
  );

  // 알림톡 수신 번호 변경 — 입력값이 있으면 급여담당자 연락처(kakaoMobileNo)로 저장,
  // 비운 채 저장하면 급여담당자 연락처를 삭제해 조직 대표 번호(userMobileNo)가 표시되게 한다.
  const handlePhoneSubmit = (digits: string) => {
    const target = phoneEditTarget;
    if (!target) return;
    const done = () => {
      setPhoneEditTarget(null);
      loadPhoneMap();
    };
    const fail = (err: any, action: string) => {
      console.error(err);
      alert(
        err.response?.data?.message || `번호 ${action} 중 오류가 발생했습니다.`
      );
    };

    if (!digits) {
      if (!target.kakaoMobileNo) {
        setPhoneEditTarget(null);
        return;
      }
      deleteKakaoMobileNo(target.businessId)
        .then(done)
        .catch((err) => fail(err, "삭제"));
      return;
    }
    updateKakaoMobileNo(target.businessId, digits)
      .then(done)
      .catch((err) => fail(err, "수정"));
  };

  // 수동 체크 — 낙관적 갱신 후 API 호출, 실패 시 롤백
  const onManualToggle = useCallback(
    (row: DerivedRow, colId: string, nextDone: boolean) => {
      const cell = COLUMN_BY_ID[colId]?.manualCell;
      if (!cell) return;
      const now = new Date().toISOString();
      const apply = (done: boolean, prev: PayrollGridRow) => ({
        ...prev,
        manualChecks: {
          ...prev.manualChecks,
          [cell]: { done, checkedAt: now, checkedByUser: myName }
        }
      });
      setGridRows((prev) =>
        prev.map((r) =>
          r.businessId === row.businessId ? apply(nextDone, r) : r
        )
      );
      patchPayrollManualCheck({
        businessId: row.businessId,
        yearMonth: period,
        cell,
        done: nextDone
      }).catch((err) => {
        console.error(err);
        setGridRows((prev) =>
          prev.map((r) =>
            r.businessId === row.businessId
              ? { ...r, manualChecks: row.manualChecks }
              : r
          )
        );
        alert(
          err.response?.data?.message ||
            "저장 중 오류가 발생했습니다. 다시 시도해 주세요."
        );
      });
    },
    [period, myName, alert]
  );

  // 거래처 예외 업무 체크리스트 — 항목 단위로 저장(2.5~2.7). 각 응답은 그 거래처의 전체 목록이라
  // 그대로 gridRows에 반영한다. 실패 시 목록은 그대로 두고 알림만 띄운다.
  const applyMemos = (businessId: string, memos: PayrollMemo[]) =>
    setGridRows((prev) =>
      prev.map((r) =>
        r.businessId === businessId ? { ...r, memos: memos || [] } : r
      )
    );
  const memoFail = (err: any, action: string) => {
    console.error(err);
    alert(
      err.response?.data?.message || `할 일 ${action} 중 오류가 발생했습니다.`
    );
  };
  // 반복 메모는 지급월(period)이 속한 주기로 완료를 기록하므로 모든 호출에 period를 넘긴다.
  const onMemoAdd =
    (businessId: string) => (text: string, repeat: MemoRepeat) =>
      addPayrollMemo({ businessId, yearMonth: period, text, repeat })
        .then((res) => applyMemos(businessId, res.data))
        .catch((err) => memoFail(err, "추가"));
  const onMemoToggle =
    (businessId: string) => (memoId: string, done: boolean) =>
      setPayrollMemoDone({ businessId, memoId, yearMonth: period, done })
        .then((res) => applyMemos(businessId, res.data))
        .catch((err) => memoFail(err, "저장"));
  const onMemoDelete = (businessId: string) => (memoId: string) =>
    deletePayrollMemo(businessId, memoId, period)
      .then((res) => applyMemos(businessId, res.data))
      .catch((err) => memoFail(err, "삭제"));

  // 거래처 자유 메모 (Organization.memoMap, §4.3). 빈 값으로 저장하면 삭제.
  const onOrgMemoSave = (businessId: string) => (text: string) => {
    const v = text.trim();
    return updateSmartCtaMemo(businessId, v)
      .then(() =>
        setMemoMap((prev) => {
          const next = new Map(prev);
          if (v) next.set(businessId, v);
          else next.delete(businessId);
          return next;
        })
      )
      .catch((err) => memoFail(err, v ? "저장" : "삭제"));
  };

  // ── 업로드/매칭/중복 흐름 (기존 로직 유지) ────────────────────
  const isUnmatched = (n: PaymentNoticeData) =>
    !n.businessId ||
    n.matchStatus === "UNMATCHED" ||
    n.matchStatus === "AMBIGUOUS";

  // 납부서 목록 팝업에서 매칭 사업자를 바꾼 직후, 같은 조합의 중복 납부서가 있으면 중복 팝업을 띄운다.
  const checkDuplicatesAfterRematch = (paymentNoticeId: string) => {
    if (!organizationId) return;
    checkDuplicatePaymentNotices([paymentNoticeId])
      .then((res) => {
        const duplicates: PaymentNoticeData[] = res.data || [];
        if (duplicates.length > 0) {
          setDupNotices(duplicates);
          setIsDupOpen(true);
        }
      })
      .catch((err) => console.error(err));
  };

  const runDupThenResult = (
    ids: string[],
    results: PaymentNoticeUploadResult[]
  ) => {
    if (!ids.length) {
      setResultList(results);
      setIsResultOpen(true);
      return;
    }
    checkDuplicatePaymentNotices(ids)
      .then((res) => {
        const dups: PaymentNoticeData[] = res.data || [];
        if (dups.length) {
          setDupNotices(dups);
          setPendingResultAfterDup(results);
          setHasPendingResultAfterDup(true);
          setIsDupOpen(true);
        } else {
          setResultList(results);
          setIsResultOpen(true);
        }
      })
      .catch(() => {
        setResultList(results);
        setIsResultOpen(true);
      });
  };

  const onUploadFinished = (results: PaymentNoticeUploadResult[]) => {
    setIsUploadOpen(false);
    setFlowSource("upload");
    loadGrid();
    checkUnmatched();
    const ids = results
      .filter((r) => r.notice)
      .map((r) => (r.notice as PaymentNoticeData).id);
    const unmatched = results
      .filter((r) => r.notice && isUnmatched(r.notice))
      .map((r) => r.notice as PaymentNoticeData);
    if (unmatched.length) {
      setPendingResults(results);
      setUnmatchedList(unmatched);
      setPendingDupIds(ids);
      setIsMatchingOpen(true);
    } else {
      runDupThenResult(ids, results);
    }
  };

  const onMatchingSuccess = (selections: Record<string, string>) => {
    setIsMatchingOpen(false);
    setUnmatchedList([]);
    loadGrid();
    checkUnmatched();
    const updated = pendingResults.map((r) =>
      r.notice && selections[r.notice.id]
        ? {
            ...r,
            notice: {
              ...r.notice,
              businessId: selections[r.notice.id],
              matchStatus: "MATCHED_MANUALLY" as const
            }
          }
        : r
    );
    setPendingResults([]);
    if (flowSource === "upload" && pendingDupIds.length) {
      const ids = pendingDupIds;
      setPendingDupIds([]);
      runDupThenResult(ids, updated);
    } else {
      setResultList(updated);
      setIsResultOpen(true);
    }
  };

  const onMatchingPartialSuccess = (selections: Record<string, string>) => {
    setUnmatchedList((prev) => prev.filter((n) => !selections[n.id]));
    setPendingResults((prev) =>
      prev.map((r) =>
        r.notice && selections[r.notice.id]
          ? {
              ...r,
              notice: {
                ...r.notice,
                businessId: selections[r.notice.id],
                matchStatus: "MATCHED_MANUALLY" as const
              }
            }
          : r
      )
    );
    loadGrid();
    checkUnmatched();
  };

  const onNoticeDeleted = (id: string) => {
    setUnmatchedList((prev) => prev.filter((n) => n.id !== id));
    setPendingResults((prev) => prev.filter((r) => r.notice?.id !== id));
    loadGrid();
    checkUnmatched();
  };

  // 납부서·접수증 업로드 버튼: 매칭 필요 납부서가 있으면 매칭 화면을 먼저 띄우고,
  // 그 화면(취소) 또는 매칭 결과 화면이 닫히면 이어서 업로드 화면을 연다.
  const handleUploadClick = () => {
    if (!organizationId) return alert("소속 조직 정보를 확인할 수 없습니다.");
    if (unmatchedCount <= 0) {
      setIsUploadOpen(true);
      return;
    }
    getUnmatchedPaymentNotices(organizationId, { size: 100 })
      .then((res) => {
        const content: PaymentNoticeData[] = res.data?.content || [];
        if (!content.length) {
          setUnmatchedCount(0);
          setIsUploadOpen(true);
          return;
        }
        setFlowSource("standalone");
        setPendingResults(
          content.map((n) => ({ fileName: n.originalFileName, notice: n }))
        );
        setUnmatchedList(content);
        setOpenUploadAfterMatch(true);
        setIsMatchingOpen(true);
      })
      .catch((err) =>
        alert(
          err.response?.data?.message ||
            "매칭 필요 목록을 조회하는 중 오류가 발생했습니다."
        )
      );
  };

  // 매칭/매칭결과 화면이 닫힌 뒤 업로드 화면으로 이어줄지 처리
  const continueToUploadIfPending = () => {
    if (openUploadAfterMatch) {
      setOpenUploadAfterMatch(false);
      setIsUploadOpen(true);
    }
  };

  const closeDup = () => {
    setIsDupOpen(false);
    setDupNotices([]);
    if (hasPendingResultAfterDup) {
      setResultList(pendingResultAfterDup);
      setPendingResultAfterDup([]);
      setHasPendingResultAfterDup(false);
      setIsResultOpen(true);
    }
  };

  return (
    <div
      // 25칸 매트릭스가 Container 좌우 여백(데스크톱 최대 120px) 안에서는 너무 좁다.
      // Container 여백을 뚫되 최대 1640px로 제한하고 뷰포트 기준 가운데 정렬한다.
      css={css`
        width: min(1640px, calc(100vw - 40px));
        margin-left: calc(50% - min(820px, (100vw - 40px) / 2));
      `}
    >
      <section
        css={css`
          padding: 48px 0;
          display: flex;
          align-items: center;
        `}
      >
        <h1
          css={css`
            font-size: 28px;
            font-weight: 500;
          `}
        >
          전체 업체 현황
        </h1>
      </section>

      <section
        css={css`
          margin-bottom: 12px;
          display: flex;
          align-items: center;
          gap: 12px;
          flex-wrap: wrap;
        `}
      >
        <BaseSelect
          name="월별검색"
          options={reverseArray(MONTHS)}
          selectedOption={period}
          onSelect={(e) =>
            setParam({ period: e.currentTarget.value || period })
          }
        />
        <BaseInput
          placeholder="업체명 검색"
          value={keyword}
          onInput={(e) => setKeyword(e.currentTarget.value)}
          css={css`
            box-sizing: border-box;
          `}
        />
        <BaseButton
          variant={isAssignMode ? "contained" : "outlined"}
          color={PALETTE.primary}
          onClick={toggleAssignMode}
          css={toolBtnCss}
        >
          담당자 설정
        </BaseButton>
        {isAssignMode && (
          <>
            <span
              css={css`
                font-size: 14px;
                color: ${PALETTE.textMid};
                white-space: nowrap;
              `}
            >
              {selected.size}건 선택됨
              {hiddenCheckedCount > 0 &&
                ` (화면에 없는 ${hiddenCheckedCount}건 포함)`}
            </span>
            <BaseSelect
              name={isInchargeOptionsLoading ? "담당자 조회 중" : "담당자 선택"}
              options={[
                ...inchargeOptions.map((n) => ({ value: n, label: n })),
                { value: NO_INCHARGE_OPTION_VALUE, label: "(담당자 없음)" }
              ]}
              selectedOption=""
              onSelect={selectIncharge}
              disabled={isInchargeOptionsLoading || selected.size === 0}
            />
          </>
        )}

        {/* 담당자 설정 모드일 때는 오른쪽 액션 버튼들을 숨긴다. */}
        {!isAssignMode && (
          <>
            <div
              css={css`
                width: 4px;
              `}
            />

            <BaseButton
              variant="outlined"
              color={ACTION_BTN_COLOR}
              onClick={() => setRetDialogOpen(true)}
              css={toolBtnCss}
            >
              퇴사자 정산요청 {retCount > 0 ? `(${retCount})` : ""}
            </BaseButton>
            <BaseButton
              variant="outlined"
              color={unmatchedCount > 0 ? PALETTE.error : ACTION_BTN_COLOR}
              onClick={handleUploadClick}
              css={toolBtnCss}
            >
              납부서·접수증 업로드
              {unmatchedCount > 0 ? ` (${unmatchedCount})` : ""}
            </BaseButton>

            {!isSendMode ? (
              <BaseButton
                variant="outlined"
                color={ACTION_BTN_COLOR}
                onClick={() => {
                  setSelected(new Set());
                  setSendFilter(null);
                  setIsAssignMode(false);
                  setIsSendMode(true);
                }}
                css={toolBtnCss}
              >
                납부서 알림톡 발송
              </BaseButton>
            ) : (
              <div
                css={css`
                  display: flex;
                  align-items: center;
                  gap: 12px;
                `}
              >
                <span
                  css={css`
                    font-size: 14px;
                    color: ${PALETTE.textMid};
                    white-space: nowrap;
                  `}
                >
                  {selectedRows.length}건 선택됨
                </span>
                <BaseButton
                  variant="contained"
                  color={PALETTE.primary}
                  disabled={selectedRows.length === 0}
                  onClick={handleSendClick}
                  css={toolBtnCss}
                >
                  발송
                </BaseButton>
                <BaseButton
                  variant="outlined"
                  color={ACTION_BTN_COLOR}
                  onClick={exitSendMode}
                  css={toolBtnCss}
                >
                  취소
                </BaseButton>
              </div>
            )}
          </>
        )}
      </section>

      <div
        css={css`
          display: flex;
          align-items: center;
          flex-wrap: wrap;
          gap: 12px;
          margin-bottom: 12px;
          font-size: 14px;
          color: ${PALETTE.textMid};

          label {
            display: flex;
            align-items: center;
            gap: 6px;
            font-size: 14px;
            color: ${PALETTE.textMid};
            cursor: pointer;
          }
        `}
      >
        <span>거래처 {filteredRows.length}개</span>
        <div>
          미완료{" "}
          {todoCount > 0 || onlyTodo ? (
            <BaseButton
              variant={onlyTodo ? "contained" : "outlined"}
              color={PALETTE.error}
              style={{
                padding: "2px 6px 1px",
                fontSize: "13px",
                transform: "translateY(-1px)"
              }}
              onClick={() => setOnlyTodo((v) => !v)}
            >
              {todoCount}개
            </BaseButton>
          ) : (
            <span>{todoCount}개</span>
          )}
        </div>
        <BaseCheck
          id="myChargeOnly"
          checked={isMyChargeOnly}
          onChange={(e) => setIsMyChargeOnly(e.target.checked)}
          label="내 담당만 보기"
        />
      </div>

      {isSendMode && (
        <div
          css={css`
            display: flex;
            align-items: center;
            flex-wrap: wrap;
            gap: 6px 14px;
            font-size: 13px;
            color: ${PALETTE.textMid};
            margin-bottom: 8px;
          `}
        >
          <span
            css={css`
              display: inline-flex;
              align-items: center;
              gap: 4px;
              flex-wrap: wrap;
            `}
          >
            발송
            <SendCountButton
              count={sendStats.sent}
              active={sendFilter === "sent"}
              onClick={() => toggleSendFilter("sent")}
            />
            <span>( 재발송 필요</span>
            <SendCountButton
              count={sendStats.resend}
              active={sendFilter === "resend"}
              onClick={() => toggleSendFilter("resend")}
            />
            <span>)</span>
          </span>

          <span
            css={css`
              display: inline-flex;
              align-items: center;
              gap: 4px;
              flex-wrap: wrap;
            `}
          >
            미발송
            <SendCountButton
              count={sendStats.unsent}
              active={sendFilter === "unsent"}
              onClick={() => toggleSendFilter("unsent")}
            />
            <span>( 발송 필요</span>
            <SendCountButton
              count={sendStats.ready}
              active={sendFilter === "ready"}
              onClick={() => toggleSendFilter("ready")}
            />
            <span>/ 발송 확인 요망</span>
            <SendCountButton
              count={sendStats.check}
              active={sendFilter === "check"}
              onClick={() => toggleSendFilter("check")}
            />
            <span>/ 미업로드</span>
            <SendCountButton
              count={sendStats.noupload}
              active={sendFilter === "noupload"}
              onClick={() => toggleSendFilter("noupload")}
            />
            <span>)</span>
          </span>

          <span
            css={css`
              padding-left: 14px;
              border-left: 1px solid ${PALETTE.border};
            `}
          >
            선택 {selectedRows.length}곳
          </span>
          {selectedRows.length === 0 && (
            <span
              css={css`
                color: ${PALETTE.primary};
              `}
            >
              발송할 거래처를 선택하세요
            </span>
          )}
        </div>
      )}

      <PayrollGrid
        rows={visibleRows}
        viewYear={viewYear}
        viewMonth={viewMonth}
        loading={isLoading}
        sort={sort}
        onSort={onSort}
        collapsed={collapsed}
        onToggleCollapse={onToggleCollapse}
        selectable={isSendMode || isAssignMode}
        selected={selected}
        onToggleSelected={onToggleSelected}
        onToggleAll={(checked) =>
          setSelected(
            checked ? new Set(visibleRows.map((r) => r.businessId)) : new Set()
          )
        }
        onSelectRange={onSelectRange}
        onManualToggle={onManualToggle}
        onRetClick={onRetClick}
        onMemoOpen={onMemoOpen}
        onNameClick={onMemoOpen}
        onNoticeCellClick={onNoticeCellClick}
        onPhoneClick={onPhoneClick}
        onDispatchClick={onDispatchClick}
      />

      <div
        css={css`
          margin-top: 8px;
          display: flex;
          flex-wrap: wrap;
          gap: 4px 16px;
          padding: 8px 12px;
          background: ${PALETTE.light};
          border: 1px solid ${PALETTE.border};
          border-radius: 10px;
          font-size: 12px;
          color: ${PALETTE.textMid};
        `}
      >
        {STATUS_LEGEND.map((l) => (
          <span
            key={l.code}
            css={css`
              display: flex;
              align-items: center;
              gap: 6px;
            `}
          >
            <LegendDot code={l.code} />
            <b
              css={css`
                color: ${PALETTE.text};
              `}
            >
              {l.label}
            </b>{" "}
            {l.desc}
          </span>
        ))}
        <span
          css={css`
            margin-left: auto;
            color: ${PALETTE.primary};
          `}
        >
          ● 클릭해 직접 완료 처리 가능
        </span>
      </div>

      {memoTarget &&
        (() => {
          const bizId = memoTarget.row.businessId;
          // 항목 저장 후 gridRows가 갱신되므로 목록은 항상 최신 파생 행에서 읽는다.
          const live =
            derivedRows.find((r) => r.businessId === bizId) || memoTarget.row;
          return (
            <MemoPopover
              businessName={live.name}
              memos={live.memos || []}
              orgMemo={live.orgMemo ?? null}
              anchorRect={memoTarget.rect}
              onClose={() => setMemoTarget(null)}
              onAdd={onMemoAdd(bizId)}
              onToggle={onMemoToggle(bizId)}
              onDelete={onMemoDelete(bizId)}
              onOrgMemoSave={onOrgMemoSave(bizId)}
            />
          );
        })()}

      <SeveranceRequestDialog
        isOpen={retDialogOpen}
        onClose={() => setRetDialogOpen(false)}
        panel={severancePanel}
        yearMonth={period}
        onChanged={() => {
          loadSeverancePanel();
          loadGrid();
        }}
      />

      <BusinessDetailDialog
        isOpen={noticeListBusinessId !== null}
        businessId={noticeListBusinessId}
        organizationId={organizationId}
        period={period}
        businessList={candidateBusinesses}
        onClose={() => setNoticeListBusinessId(null)}
        onNoticeDeleted={() => {
          loadGrid();
          checkUnmatched();
        }}
        onNoticeRematched={(paymentNoticeId) => {
          loadGrid();
          checkUnmatched();
          checkDuplicatesAfterRematch(paymentNoticeId);
        }}
      />

      <KakaoHistoryDialog
        isOpen={kakaoHistoryTarget !== null}
        businessId={kakaoHistoryTarget?.businessId || null}
        businessName={kakaoHistoryTarget?.name || ""}
        period={period}
        onClose={() => setKakaoHistoryTarget(null)}
      />

      <PhoneEditDialog
        isOpen={phoneEditTarget !== null}
        businessName={phoneEditTarget?.name || ""}
        currentNumber={
          phoneEditTarget
            ? phoneEditTarget.kakaoMobileNo ||
              phoneEditTarget.userMobileNo ||
              null
            : null
        }
        hasKakaoMobileNo={!!phoneEditTarget?.kakaoMobileNo}
        onClose={() => setPhoneEditTarget(null)}
        onSubmit={handlePhoneSubmit}
      />

      <ConfirmDialog
        isOpen={pendingSendRows.length > 0}
        onCancel={() => setPendingSendRows([])}
        onConfirm={() => {
          openKakaoDialog(pendingSendRows);
          setPendingSendRows([]);
        }}
        confirmLabel="발송"
      >
        <DialogContentText>
          이미 카톡을 발송한 거래처가 포함되어 있습니다. 발송하시겠습니까?
        </DialogContentText>
        <DialogContentText>
          (
          {pendingSendRows
            .filter((r) => r.dispatched)
            .map((r) => r.name)
            .join(", ")}
          )
        </DialogContentText>
      </ConfirmDialog>

      <ConfirmDialog
        isOpen={isInchargeConfirmOpen}
        onCancel={closeInchargeConfirm}
        onConfirm={confirmAssignIncharge}
        disabled={isInchargeSubmitting}
      >
        <DialogContentText>
          선택한 {selected.size}건을 &quot;{pendingInchargeLabel}&quot; 담당자로
          지정하시겠습니까?
        </DialogContentText>
        {hiddenCheckedCount > 0 && (
          <DialogContentText>
            이 중 {hiddenCheckedCount}건은 현재 필터에 가려져 화면에 보이지 않는
            거래처입니다.
          </DialogContentText>
        )}
      </ConfirmDialog>

      <KakaoSendDialog
        isOpen={kakaoTargets.length > 0}
        businesses={kakaoTargets}
        period={period}
        onClose={() => {
          setKakaoTargets([]);
          exitSendMode();
        }}
        onFinished={loadGrid}
      />

      <UploadDialog
        isOpen={isUploadOpen}
        organizationId={organizationId}
        onClose={() => setIsUploadOpen(false)}
        onUploadFinished={onUploadFinished}
      />
      <MatchingDialog
        isOpen={isMatchingOpen}
        organizationId={organizationId}
        unmatchedList={unmatchedList}
        businessList={candidateBusinesses}
        onClose={() => {
          setIsMatchingOpen(false);
          setUnmatchedList([]);
          setPendingResults([]);
          continueToUploadIfPending();
        }}
        onSuccess={onMatchingSuccess}
        onPartialSuccess={onMatchingPartialSuccess}
        onDeleted={onNoticeDeleted}
        showCloseButton={flowSource === "standalone"}
      />
      <DuplicateCheckDialog
        isOpen={isDupOpen}
        organizationId={organizationId}
        duplicateList={dupNotices}
        businessList={candidateBusinesses}
        onClose={closeDup}
        onChanged={() => {
          loadGrid();
          checkUnmatched();
        }}
      />
      <ResultDialog
        isOpen={isResultOpen}
        title={flowSource === "upload" ? "업로드 결과" : "매칭 결과"}
        results={resultList}
        onClose={() => {
          setIsResultOpen(false);
          setResultList([]);
          continueToUploadIfPending();
        }}
      />

      <AlertDialog
        isOpen={isAlertOpen}
        setIsOpen={setIsAlertOpen}
        alertData={alertData}
      />
    </div>
  );
}

const toolBtnCss = css`
  padding: 11px 16px;
  font-size: 14px;
  white-space: nowrap;
`;

// 오른쪽 액션 버튼(퇴사자 정산요청 · 업로드 · 알림톡 발송) 공통 색.
// 채움은 흰색 그대로 두고, 테두리·글자색만 검정으로 해서 왼쪽 검색/필터(보라)와 구분한다.
const ACTION_BTN_COLOR = "#000000";

// 발송 요약 집계의 클릭 가능한 건수 버튼 (조치 필요 버튼과 같은 스타일)
function SendCountButton({
  count,
  active,
  onClick
}: {
  count: number;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <BaseButton
      variant={active ? "contained" : "outlined"}
      color={PALETTE.primary}
      style={{
        padding: "2px 6px 1px",
        fontSize: "13px",
        transform: "translateY(-1px)"
      }}
      onClick={onClick}
    >
      {`${count}개`}
    </BaseButton>
  );
}

function LegendDot({ code }: { code: StatusCode }) {
  const map: Record<string, { color: string; filled: boolean }> = {
    done: { color: PALETTE.progressCompleted, filled: true },
    todo: { color: "#F5A623", filled: false },
    refund: { color: PALETTE.progressCompleted, filled: false },
    alert: { color: PALETTE.error, filled: true },
    off: { color: PALETTE.textSub, filled: false }
  };
  const m = map[code] || map.off;
  if (code === "off")
    return (
      <span
        css={css`
          color: ${PALETTE.textSub};
        `}
      >
        –
      </span>
    );
  return (
    <span
      css={css`
        display: inline-block;
        width: 10px;
        height: 10px;
        border-radius: 3px;
        ${m.filled
          ? `background:${m.color};`
          : `box-shadow: inset 0 0 0 2px ${m.color};`}
      `}
    />
  );
}

export default PayrollManagement;
